import React, { useEffect, useRef, useState } from 'react';
import { useGoogleOAuth } from '@react-oauth/google';
import { useGoogleClientId } from '../AsyncGoogleOAuthProvider';
import { googleSignInErrorMessage } from '../../utils/googleOAuthOriginHelp';
import { renderGoogleSignInButton, setGoogleSignInHandlers } from '../../utils/googleGsi';

function GoogleMark({ className = 'h-5 w-5' }) {
  return (
    <svg className={className} viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#FFC107"
        d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
      />
      <path
        fill="#FF3D00"
        d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
      />
    </svg>
  );
}

function gsiControlMounted(container) {
  if (!container) return false;
  return Boolean(
    container.querySelector('iframe')
      || container.querySelector('[role="button"]')
      || container.firstElementChild
  );
}

function AuthGoogleButtonInner({
  onSuccess,
  onError,
  modoOscuro,
  text = 'signin_with',
  className = '',
}) {
  const { scriptLoadedSuccessfully } = useGoogleOAuth();
  const googleClientId = useGoogleClientId();
  const wrapRef = useRef(null);
  const gsiButtonRef = useRef(null);
  const [buttonWidth, setButtonWidth] = useState(0);
  const [gsiMounted, setGsiMounted] = useState(false);
  const [loadSlow, setLoadSlow] = useState(false);

  const label = text === 'signup_with' ? 'Registrarse con Google' : 'Acceder con Google';
  const canMountGsi = scriptLoadedSuccessfully && Boolean(googleClientId) && buttonWidth > 0;

  useEffect(() => {
    setGoogleSignInHandlers({
      onSuccess,
      onError: () => onError?.({ reason: 'google' }),
    });
    return () => setGoogleSignInHandlers({ onSuccess: null, onError: null });
  }, [onSuccess, onError]);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;

    const measure = () => {
      const w = Math.max(Math.floor(el.getBoundingClientRect().width), 280);
      setButtonWidth(w);
    };

    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    window.addEventListener('resize', measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  useEffect(() => {
    if (!canMountGsi || !gsiButtonRef.current) {
      setGsiMounted(false);
      return undefined;
    }

    setGsiMounted(false);
    setLoadSlow(false);

    renderGoogleSignInButton(gsiButtonRef.current, {
      type: 'standard',
      theme: modoOscuro ? 'filled_black' : 'outline',
      size: 'large',
      text,
      shape: 'rectangular',
      locale: 'es',
      width: buttonWidth,
    });

    let attempts = 0;
    const interval = window.setInterval(() => {
      attempts += 1;
      if (gsiControlMounted(gsiButtonRef.current)) {
        setGsiMounted(true);
        setLoadSlow(false);
        window.clearInterval(interval);
      } else if (attempts > 120) {
        setLoadSlow(true);
        window.clearInterval(interval);
      }
    }, 100);

    const slowTimer = window.setTimeout(() => {
      if (!gsiControlMounted(gsiButtonRef.current)) setLoadSlow(true);
    }, 4000);

    return () => {
      window.clearInterval(interval);
      window.clearTimeout(slowTimer);
    };
  }, [canMountGsi, buttonWidth, text, modoOscuro]);

  const shellClass = modoOscuro
    ? 'border-gray-700 bg-[#131314] text-white'
    : 'border-gray-300 bg-white text-gray-800 shadow-sm';

  const hitLayerActive = canMountGsi;

  return (
    <div ref={wrapRef} className={`relative w-full min-h-[44px] ${className}`.trim()}>
      <div
        className={`pointer-events-none w-full min-h-[44px] px-4 py-2.5 rounded-xl border text-sm font-semibold flex items-center justify-center gap-3 ${shellClass}`}
        aria-hidden="true"
      >
        <GoogleMark />
        <span>{label}</span>
      </div>

      <div
        className={`absolute inset-0 z-10 overflow-hidden rounded-xl ${
          hitLayerActive ? 'cursor-pointer opacity-[0.001]' : 'pointer-events-none opacity-0'
        }`}
        aria-label={label}
      >
        <div
          ref={gsiButtonRef}
          className="flex h-full min-h-[44px] w-full items-stretch justify-center [&_iframe]:min-h-[44px] [&_iframe]:w-full [&_iframe]:max-w-none"
        />
      </div>

      {loadSlow && !gsiMounted && (
        <p className="mt-2 text-center text-[11px] text-amber-600 dark:text-amber-400">
          Google tarda en cargar. Recargá la página o probá de nuevo en unos segundos.
        </p>
      )}
    </div>
  );
}

export default function AuthGoogleButton({ onError, ...props }) {
  const googleClientId = useGoogleClientId();
  const handleError = onError || ((detail) => {
    if (typeof window !== 'undefined') {
      console.warn('[Google Sign-In]', googleSignInErrorMessage({ clientId: googleClientId, ...detail }));
    }
  });

  if (!googleClientId) {
    return (
      <p className="text-xs text-center text-gray-500 py-2">
        Login con Google no disponible (falta configurar el Client ID en Vercel).
      </p>
    );
  }

  return <AuthGoogleButtonInner {...props} onError={handleError} />;
}
